import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AutoComplete,
  Button,
  Card,
  Empty,
  Form,
  Grid,
  Image,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import {
  InboxOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { createTask, fetchTaskTypes } from '../../store/slices/taskSlice';
import { uploadApi } from '../../services/upload';
import { monitoringApi } from '../../services/monitoring';
import { RecentProcessedTaskRecord } from '../../types';

const { Text, Paragraph, Link } = Typography;

const imageExtensions = ['.png', '.jpg', '.jpeg', '.webp', '.gif'];
const videoExtensions = ['.mp4', '.mov', '.webm', '.mkv'];

const isImageUrl = (value: string) => {
  const lowerValue = value.toLowerCase();
  return imageExtensions.some((extension) => lowerValue.includes(extension));
};

const isVideoUrl = (value: string) => {
  const lowerValue = value.toLowerCase();
  return videoExtensions.some((extension) => lowerValue.includes(extension));
};

const formatDuration = (value?: number | null) => {
  if (value === null || value === undefined) {
    return '-';
  }
  if (value < 1000) {
    return `${value} ms`;
  }
  return `${(value / 1000).toFixed(value >= 10_000 ? 1 : 2)} s`;
};

// ==================== 任务创建 ====================

const priorityOptions = [
  { label: '普通 (1)', value: 1 },
  { label: 'VIP (2)', value: 2 },
];

const envOptions = [
  { label: 'plus-pre', value: 'plus-pre' },
  { label: 'plus', value: 'plus' },
];

// Seedream 5.0 Pro 生图任务，详见 doc/SEEDREAM_API_DOCUMENTATION.md
const SEEDREAM_TASK_TYPE = 250002;
const seedreamSizeOptions = [
  { label: '2K（默认）', value: '2K' },
  { label: '1K', value: '1K' },
  { label: '1:1', value: '1:1' },
  { label: '4:3', value: '4:3' },
  { label: '3:4', value: '3:4' },
  { label: '16:9', value: '16:9' },
  { label: '9:16', value: '9:16' },
  { label: '3:2', value: '3:2' },
  { label: '2:3', value: '2:3' },
  { label: '21:9', value: '21:9' },
];
const seedreamResolutionOptions = [
  { label: '2K', value: '2K' },
  { label: '1K', value: '1K' },
];

interface CreateResult {
  taskId: string;
  displayId: number;
  status: string;
  estimatedWaitTime: number;
}

const TaskCreatePanel: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { taskTypes } = useSelector((state: RootState) => state.task);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const apiKey = useSelector((state: RootState) => state.settings.apiKey);
  const screens = Grid.useBreakpoint();

  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<CreateResult | null>(null);
  // 缓存已上传成功的图片路径（uid -> GCS path），避免重复上传同一张图
  const uploadedPathCache = useRef<Record<string, string>>({});

  useEffect(() => {
    if (!taskTypes.length) {
      dispatch(fetchTaskTypes());
    }
  }, [dispatch, taskTypes.length]);

  const taskTypeOptions = useMemo(
    () =>
      taskTypes.map((item) => ({
        label: `${item.name} (${item.type})`,
        value: String(item.type),
      })),
    [taskTypes]
  );

  const durationWatch = Form.useWatch('duration', form);
  const promptWatch = Form.useWatch('prompt', form);
  const taskTypeWatch = Form.useWatch('taskType', form);
  const sizeWatch = Form.useWatch('size', form);
  const resolutionWatch = Form.useWatch('resolution', form);
  const watermarkWatch = Form.useWatch('watermark', form);

  const isSeedream = Number(taskTypeWatch) === SEEDREAM_TASK_TYPE;

  const payloadPreview = useMemo(() => {
    const payload: Record<string, unknown> = {};
    // 图片路径在点击“创建任务”时才上传生成，这里用占位提示结构（img1..imgN）
    fileList.forEach((_, index) => {
      payload[`img${index + 1}`] = '<上传后自动生成的图片路径>';
    });
    if (promptWatch !== undefined && promptWatch !== null && String(promptWatch).trim() !== '') {
      payload.prompt = promptWatch;
    }
    if (isSeedream) {
      // Seedream 生图参数
      if (sizeWatch) {
        payload.size = sizeWatch;
      }
      if (resolutionWatch) {
        payload.resolution = resolutionWatch;
      }
      if (watermarkWatch) {
        payload.watermark = true;
      }
    } else if (durationWatch !== undefined && durationWatch !== null && durationWatch !== '') {
      payload.duration = durationWatch;
    }
    return JSON.stringify(payload, null, 2);
  }, [fileList, durationWatch, promptWatch, isSeedream, sizeWatch, resolutionWatch, watermarkWatch]);

  // 阻止 antd 自动上传：仅把图片收集到列表，点击“创建任务”时再统一上传
  const handleUploadChange: UploadProps['onChange'] = ({ fileList: nextList }) => {
    setFileList(nextList);
  };

  /** 上传列表中的图片，返回它们的 GCS path（已上传过的走缓存）。 */
  const uploadFiles = async (): Promise<string[]> => {
    const paths: string[] = [];
    for (const item of fileList) {
      const cached = uploadedPathCache.current[item.uid];
      if (cached) {
        paths.push(cached);
        continue;
      }
      const fileObj = (item.originFileObj as File | undefined) || (item as unknown as File);
      if (!fileObj) {
        continue;
      }
      const uploaded = await uploadApi.uploadImage(fileObj, apiKey);
      uploadedPathCache.current[item.uid] = uploaded.path;
      paths.push(uploaded.path);
    }
    return paths;
  };

  const handleSubmit = async (values: {
    taskType: string;
    duration?: number;
    prompt?: string;
    size?: string;
    resolution?: string;
    watermark?: boolean;
    credits?: number;
    userId?: string;
    app?: string;
    priority: number;
    env?: string;
  }) => {
    const parsedTaskType = Number(values.taskType);
    if (!Number.isFinite(parsedTaskType)) {
      message.error('任务类型需要是数字');
      return;
    }
    const seedream = parsedTaskType === SEEDREAM_TASK_TYPE;
    // Seedream 支持文生图（0 张参考图），其它任务仍要求至少一张图
    if (!seedream && !fileList.length) {
      message.error('请至少上传一张图片');
      return;
    }

    setSubmitting(true);
    try {
      // 先把图片上传到服务器，拿到 path，再创建任务
      const paths = fileList.length ? await uploadFiles() : [];
      if (fileList.length && !paths.length) {
        message.error('图片上传失败，请重试');
        return;
      }

      const payload: Record<string, unknown> = {};
      paths.forEach((path, index) => {
        payload[`img${index + 1}`] = path;
      });

      const trimmedPrompt = values.prompt?.trim();
      if (trimmedPrompt) {
        payload.prompt = trimmedPrompt;
      }

      if (seedream) {
        if (values.size) {
          payload.size = values.size;
        }
        if (values.resolution) {
          payload.resolution = values.resolution;
        }
        if (values.watermark) {
          payload.watermark = true;
        }
      } else if (values.duration !== undefined && values.duration !== null) {
        payload.duration = values.duration;
      }

      const created = await dispatch(
        createTask({
          taskType: parsedTaskType,
          userId: values.userId?.trim() || String(currentUser?.userId ?? 'admin'),
          app: values.app?.trim() || 'admin_test',
          priority: values.priority,
          payload: JSON.stringify(payload),
          env: values.env?.trim() || undefined,
          credits: typeof values.credits === 'number' ? values.credits : undefined,
          apiKey,
        })
      ).unwrap();
      setResult(created as CreateResult);
      message.success('任务创建成功');
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : typeof error === 'string' ? error : '任务创建失败'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setFileList([]);
    uploadedPathCache.current = {};
    setResult(null);
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ priority: 1, env: 'plus-pre', app: 'admin_test' }}
      >
        <Form.Item
          label="任务图片"
          required={!isSeedream}
          extra={
            isSeedream
              ? 'Seedream 支持文生图（可不传图）、单图或多图（最多 10 张）。按 img1…imgN 顺序写入 payload。'
              : '支持选择一张或两张图片，点击“创建任务”时会先上传图片再建任务（img1 / img2）。'
          }
        >
          <Upload
            listType="picture-card"
            accept="image/*"
            fileList={fileList}
            beforeUpload={() => false}
            onChange={handleUploadChange}
            maxCount={isSeedream ? 10 : 2}
          >
            {fileList.length >= (isSeedream ? 10 : 2) ? null : (
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>选择图片</div>
              </div>
            )}
          </Upload>
        </Form.Item>

        <Form.Item
          name="taskType"
          label="任务类型"
          rules={[{ required: true, message: '请选择或输入任务类型' }]}
          extra="可从已有任务类型中选择，也可直接输入任务类型编号。"
        >
          <AutoComplete
            options={taskTypeOptions}
            placeholder="选择或输入任务类型编号"
            filterOption={(input, option) =>
              String(option?.label ?? '').toLowerCase().includes(input.toLowerCase()) ||
              String(option?.value ?? '').includes(input)
            }
            allowClear
          />
        </Form.Item>

        {!isSeedream && (
          <Form.Item name="duration" label="时长">
            <InputNumber
              min={0}
              style={{ width: 220 }}
              addonAfter="秒"
              placeholder="请输入时长"
            />
          </Form.Item>
        )}

        <Form.Item
          name="prompt"
          label="Prompt"
          rules={
            isSeedream ? [{ required: true, message: 'Seedream 任务必须填写 prompt' }] : []
          }
          extra={
            isSeedream
              ? 'Seedream 生图提示词，必填，建议不超过 300 汉字 / 600 英文单词。'
              : '创建任务时会写入 payload 的 prompt 字段（可选）。'
          }
        >
          <Input.TextArea rows={3} placeholder="请输入 prompt" allowClear />
        </Form.Item>

        {isSeedream && (
          <Space size={screens.xs ? 8 : 16} wrap style={{ display: 'flex' }}>
            <Form.Item
              name="size"
              label="尺寸 size"
              style={{ minWidth: 200 }}
              extra="宽高比/1K/2K/像素值，留空默认 2K"
            >
              <AutoComplete
                options={seedreamSizeOptions}
                placeholder="如 9:16 / 2K / 2048x2048"
                allowClear
              />
            </Form.Item>
            <Form.Item
              name="resolution"
              label="分辨率 resolution"
              style={{ minWidth: 200 }}
              extra="仅当 size 为宽高比时生效"
            >
              <Select options={seedreamResolutionOptions} placeholder="1K / 2K" allowClear />
            </Form.Item>
            <Form.Item
              name="watermark"
              label="水印 watermark"
              valuePropName="checked"
              initialValue={false}
            >
              <Switch />
            </Form.Item>
          </Space>
        )}

        <Space size={screens.xs ? 8 : 16} wrap style={{ display: 'flex' }}>
          <Form.Item name="priority" label="优先级" style={{ minWidth: 160 }}>
            <Select options={priorityOptions} />
          </Form.Item>
          <Form.Item name="env" label="环境" style={{ minWidth: 160 }}>
            <AutoComplete options={envOptions} placeholder="如 plus / plus-pre" allowClear />
          </Form.Item>
          <Form.Item name="credits" label="Credits" style={{ minWidth: 160 }}>
            <InputNumber min={0} style={{ width: '100%' }} placeholder="可选" />
          </Form.Item>
        </Space>

        <Space size={screens.xs ? 8 : 16} wrap style={{ display: 'flex' }}>
          <Form.Item name="userId" label="用户 ID" style={{ minWidth: 200 }}>
            <Input placeholder={`默认：${currentUser?.userId ?? 'admin'}`} allowClear />
          </Form.Item>
          <Form.Item name="app" label="App" style={{ minWidth: 200 }}>
            <Input placeholder="默认：admin_test" allowClear />
          </Form.Item>
        </Space>

        <Form.Item label="Payload 预览">
          <Input.TextArea
            value={payloadPreview}
            readOnly
            autoSize={{ minRows: 4, maxRows: 10 }}
            style={{ fontFamily: 'monospace' }}
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<ThunderboltOutlined />}
              loading={submitting}
            >
              {submitting ? '上传并创建中…' : '创建任务'}
            </Button>
            <Button onClick={handleReset}>重置</Button>
          </Space>
        </Form.Item>
      </Form>

      {result && (
        <Card size="small" title="创建结果" style={{ marginTop: 8 }}>
          <Paragraph style={{ marginBottom: 4 }}>
            任务 ID：<Text copyable>{result.taskId}</Text>
          </Paragraph>
          <Paragraph style={{ marginBottom: 4 }}>显示 ID：{result.displayId}</Paragraph>
          <Paragraph style={{ marginBottom: 4 }}>
            状态：<Tag color="blue">{result.status}</Tag>
          </Paragraph>
          <Paragraph style={{ marginBottom: 0 }}>
            预计等待：{result.estimatedWaitTime} 秒
          </Paragraph>
        </Card>
      )}
    </div>
  );
};

// ==================== 历史任务 ====================

const MediaPreview: React.FC<{ urls: string[]; files: string[]; kind: 'source' | 'result' }> = ({
  urls,
  files,
  kind,
}) => {
  const limitedUrls = urls.slice(0, 3);

  if (!urls.length) {
    return <Text type="secondary">-</Text>;
  }

  return (
    <Space wrap size={[8, 8]}>
      {limitedUrls.map((url, index) => {
        const file = files[index] || url;
        const mediaLabel = kind === 'source' ? `原图${index + 1}` : `结果${index + 1}`;

        if (isVideoUrl(url) || isVideoUrl(file)) {
          return (
            <div key={`${url}-${index}`} style={{ width: 96 }}>
              <video
                src={url}
                controls
                preload="metadata"
                style={{
                  width: 96,
                  height: 96,
                  objectFit: 'cover',
                  borderRadius: 8,
                  background: '#000',
                  display: 'block',
                }}
              />
              <Link href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
                {mediaLabel}
              </Link>
            </div>
          );
        }

        if (isImageUrl(url) || isImageUrl(file)) {
          return (
            <div key={`${url}-${index}`} style={{ width: 96 }}>
              <Image
                src={url}
                alt={mediaLabel}
                width={96}
                height={96}
                style={{ objectFit: 'cover', borderRadius: 8 }}
                fallback=""
              />
              <Link href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
                {mediaLabel}
              </Link>
            </div>
          );
        }

        return (
          <div key={`${url}-${index}`} style={{ maxWidth: 120 }}>
            <Tag color="default" style={{ marginBottom: 4 }}>
              文件
            </Tag>
            <div>
              <Link href={url} target="_blank" rel="noopener noreferrer">
                查看文件
              </Link>
            </div>
          </div>
        );
      })}
      {urls.length > limitedUrls.length && (
        <Tag color="blue">+{urls.length - limitedUrls.length} 个文件</Tag>
      )}
    </Space>
  );
};

const TaskHistoryPanel: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { taskTypes } = useSelector((state: RootState) => state.task);
  const screens = Grid.useBreakpoint();

  const [form] = Form.useForm();
  const [records, setRecords] = useState<RecentProcessedTaskRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, size: 20, total: 0 });
  const paginationRef = useRef(pagination);

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  useEffect(() => {
    if (!taskTypes.length) {
      dispatch(fetchTaskTypes());
    }
  }, [dispatch, taskTypes.length]);

  const taskTypeOptions = useMemo(
    () => taskTypes.map((item) => ({ label: `${item.name} (${item.type})`, value: item.type })),
    [taskTypes]
  );

  const refreshData = useCallback(
    async (page?: number, size?: number) => {
      const nextPage = page ?? paginationRef.current.current;
      const nextSize = size ?? paginationRef.current.size;
      setLoading(true);
      try {
        const values = form.getFieldsValue();
        const response = await monitoringApi.getRecentProcessedTasks({
          taskType: values.taskType || undefined,
          page: nextPage,
          size: nextSize,
        });
        const data = response.data;
        const sortedRecords = [...data.records].sort((a, b) => b.createdAt - a.createdAt);
        const nextPagination = { current: data.current, size: data.size, total: data.total };
        setRecords(sortedRecords);
        paginationRef.current = nextPagination;
        setPagination(nextPagination);
      } catch (error) {
        message.error('获取历史任务失败');
      } finally {
        setLoading(false);
      }
    },
    [form]
  );

  useEffect(() => {
    refreshData(1, paginationRef.current.size);
  }, [refreshData]);

  const handleSearch = () => refreshData(1, pagination.size);

  const handleReset = () => {
    form.resetFields();
    refreshData(1, pagination.size);
  };

  const handleTableChange = (nextPagination: TablePaginationConfig) => {
    refreshData(nextPagination.current || 1, nextPagination.pageSize || pagination.size);
  };

  const columns: ColumnsType<RecentProcessedTaskRecord> = [
    {
      title: '任务 ID',
      dataIndex: 'taskId',
      key: 'taskId',
      width: screens.xs ? 160 : 220,
      ellipsis: true,
      fixed: screens.xs ? 'left' : undefined,
    },
    {
      title: '显示 ID',
      dataIndex: 'displayId',
      key: 'displayId',
      width: 100,
      responsive: ['sm'] as any,
    },
    {
      title: '任务类型',
      key: 'taskType',
      width: screens.xs ? 130 : 180,
      render: (_, record) => (
        <div>
          <div>{record.taskName || '-'}</div>
          <Text type="secondary">{record.taskType}</Text>
        </div>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (value: number) => new Date(value).toLocaleString(),
    },
    {
      title: '执行时长',
      dataIndex: 'processingTime',
      key: 'processingTime',
      width: 110,
      render: (value: number | null | undefined) => formatDuration(value),
    },
    {
      title: '原始图片',
      key: 'sourceFileUrls',
      width: 340,
      render: (_, record) => (
        <MediaPreview urls={record.sourceFileUrls || []} files={record.sourceFiles || []} kind="source" />
      ),
    },
    {
      title: '结果媒体',
      key: 'resultFileUrls',
      width: 340,
      render: (_, record) => (
        <MediaPreview urls={record.resultFileUrls || []} files={record.resultFiles || []} kind="result" />
      ),
    },
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Form form={form} layout={screens.xs ? 'vertical' : 'inline'} onFinish={handleSearch}>
          <Form.Item name="taskType" label="任务类型" style={{ marginBottom: 8 }}>
            <Select
              allowClear
              showSearch
              placeholder="全部任务类型"
              style={{ width: screens.xs ? '100%' : 260 }}
              options={taskTypeOptions}
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 8 }}>
            <Space wrap>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                查询
              </Button>
              <Button onClick={handleReset}>重置</Button>
              <Button icon={<ReloadOutlined />} onClick={() => refreshData()} loading={loading}>
                刷新
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card>
        <Table
          rowKey="taskId"
          loading={loading}
          dataSource={records}
          columns={columns}
          scroll={{ x: 1400 }}
          locale={{
            emptyText: <Empty description="暂无历史任务" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.size,
            total: pagination.total,
            showSizeChanger: !screens.xs,
            showTotal: (total) => `共 ${total} 条记录`,
            size: screens.xs ? 'small' : 'default',
          }}
          onChange={handleTableChange}
          size={screens.xs ? 'small' : 'middle'}
        />
      </Card>
    </div>
  );
};

// ==================== 页面 ====================

const TaskTestTemplate: React.FC = () => {
  const screens = Grid.useBreakpoint();

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: screens.xs ? '18px' : '24px' }}>任务测试模版</h1>
        <Text type="secondary">上传图片、选择任务类型并填写时长以创建测试任务，并可查看历史任务。</Text>
      </div>

      <Tabs
        defaultActiveKey="create"
        items={[
          {
            key: 'create',
            label: (
              <span>
                <InboxOutlined /> 任务创建
              </span>
            ),
            children: <TaskCreatePanel />,
          },
          {
            key: 'history',
            label: (
              <span>
                <SearchOutlined /> 历史任务
              </span>
            ),
            children: <TaskHistoryPanel />,
          },
        ]}
      />
    </div>
  );
};

export default TaskTestTemplate;
